import { ChangeEvent } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import {
  strategyMatrixOptions,
  volumeByLengthOfHaulRows,
  volumeByLengthOfHaulColumns,
  desirabilityByPickupMarketRows,
  desirabilityByPickupMarketColumns,
  equipmentToPickupMarketRows,
  equipmentToPickupMarketColumns,
  volumeToDesirabilityRows,
  volumeToDesirabilityLowColumns,
  volumeToDesirabilityMediumColumns,
  volumeToDesirabilityHighColumns,
  lengthOfHaulToCostPctDat7Rows,
  lengthOfHaulToCostPctDat7Columns,
  equipmentOptions,
} from 'apps/marketplace/components/rfp/constants/strategyManager'
import {
  IMatrixRowOrColumn,
  ILaneCriteria,
  IDropStrategyOption,
  ICriteriaCondition,
  StrategyMatrix,
  MatrixAccessOrders,
  ValidStrategyMatrices,
  ValidCriteriaConditions,
  DropTypes,
  ConditionTypes,
  Strategy,
  RiskyAuditLevels,
} from 'apps/marketplace/components/rfp/types/strategyManager'

const defaultModifier = (n: string): string => n.toString()

export const isValidNumericValue = (n: string | number): boolean => {
  return !Number.isNaN(Number(n)) && n !== ''
}

const isValidMatrixValue = (n: string): boolean => {
  return isValidNumericValue(n)
}

// DA: not all strategy matrix JSON is stored in the same format:
// some have the columns as the outer keys while others have the
// rows as the outer keys, so we need this helper to standardize
// the formatting before passing the resulting matrix to the
// InputMatrix component:
export const getValueFromStrategyData = (
  data: StrategyMatrix | undefined,
  rowKey: string,
  columnKey: string,
  accessOrder: MatrixAccessOrders,
  modifier?: (n: string) => string
): string => {
  if (!data) return ''

  const dataModifier = modifier || defaultModifier

  if (accessOrder === MatrixAccessOrders.rowFirst) {
    return data[rowKey] !== undefined && data[rowKey][columnKey] !== undefined
      ? dataModifier(data[rowKey][columnKey] as string)
      : ''
  }

  return data[columnKey] !== undefined && data[columnKey][rowKey] !== undefined
    ? dataModifier(data[columnKey][rowKey] as string)
    : ''
}

export const getStrategyNameByKey = (key: ValidStrategyMatrices): string => {
  const strategy = strategyMatrixOptions.find(s => s.value === key)

  if (strategy) {
    return strategy.label
  }

  return ''
}

export const floatToPercentage = (n: number): number => {
  return Number((n * 100).toFixed(2))
}

export const percentageToFloat = (n: number): number => {
  return n / 100
}

export const buildMatrix = (
  rows: IMatrixRowOrColumn[],
  columns: IMatrixRowOrColumn[],
  data: StrategyMatrix | undefined,
  accessOrder: MatrixAccessOrders,
  onValueChange: (columnValue: string, rowValue: string, newValue: string) => void,
  modifier?: (n: string) => string
) => {
  const rowLabels = rows.map(row => row.label)
  const columnLabels = columns.map(column => column.label)
  let matrixIsValid = true

  const matrix = rows.map(row => {
    const { value: rowKey } = row

    return columns.map(column => {
      const { value: columnKey } = column
      const value = getValueFromStrategyData(data, rowKey, columnKey, accessOrder, modifier)
      const isValid = isValidMatrixValue(value)

      // DA: If any value in the matrix is invalid,
      // the whole matrix is considered invalid
      // (meaning it can't be saved to the DB in its
      // current state):
      if (!isValid) {
        matrixIsValid = false
      }

      return {
        value,
        isValid,
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          onValueChange(columnKey, rowKey, e.target.value)
        },
      }
    })
  })

  return {
    isValid: matrixIsValid,
    matrix,
    rowLabels,
    columnLabels,
  }
}

export const buildVolumeByLengthOfHaulMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (lengthOfHaul: string, volume: string, newValue: string) => void
) => {
  return buildMatrix(
    volumeByLengthOfHaulRows,
    volumeByLengthOfHaulColumns,
    data,
    MatrixAccessOrders.columnFirst,
    onValueChange
  )
}

export const buildDesirabilityByPickupMarketMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (puMarketType: string, desirabilityLevel: string, newValue: string) => void
) => {
  return buildMatrix(
    desirabilityByPickupMarketRows,
    desirabilityByPickupMarketColumns,
    data,
    MatrixAccessOrders.rowFirst,
    onValueChange
  )
}

export const buildEquipmentByPickupMarketMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (puMarketType: string, equipmentType: string, newValue: string) => void
) => {
  return buildMatrix(
    equipmentToPickupMarketRows,
    equipmentToPickupMarketColumns,
    data,
    MatrixAccessOrders.rowFirst,
    onValueChange
  )
}

export const buildVolumeToDesirabilityLowMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (desirability: string, volume: string, newValue: string) => void
) => {
  return buildMatrix(
    volumeToDesirabilityRows,
    volumeToDesirabilityLowColumns,
    data,
    MatrixAccessOrders.columnFirst,
    onValueChange
  )
}

export const buildVolumeToDesirabilityMediumMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (desirability: string, volume: string, newValue: string) => void
) => {
  return buildMatrix(
    volumeToDesirabilityRows,
    volumeToDesirabilityMediumColumns,
    data,
    MatrixAccessOrders.columnFirst,
    onValueChange
  )
}

export const buildVolumeToDesirabilityHighMatrix = (
  data: StrategyMatrix | undefined,
  onValueChange: (desirability: string, volume: string, newValue: string) => void
) => {
  return buildMatrix(
    volumeToDesirabilityRows,
    volumeToDesirabilityHighColumns,
    data,
    MatrixAccessOrders.columnFirst,
    onValueChange
  )
}

export const buildLengthOfHaulToCostPctDat7Matrix = (
  data: Record<string, number> | undefined,
  onValueChange: (lengthOfHaul: string, value: string) => void
) => {
  let matrixIsValid = true
  const row = lengthOfHaulToCostPctDat7Columns.map(column => {
    const value = data ? data[column.value].toString() : ''
    const isValid = isValidMatrixValue(value)

    // DA: if any value in the matrix is invalid,
    // the entire matrix is considered invalid.
    // This is to prevent us from saving bad data
    // to the database:
    if (!isValid) {
      matrixIsValid = false
    }

    return {
      value,
      isValid,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        onValueChange(column.value, e.target.value)
      },
    }
  })
  const matrix = [row]
  const rowLabels = lengthOfHaulToCostPctDat7Rows.map(row => row.label)
  const columnLabels = lengthOfHaulToCostPctDat7Columns.map(column => column.label)

  return {
    isValid: matrixIsValid,
    matrix,
    rowLabels,
    columnLabels,
  }
}

export const generateDropStrategyOptions = (
  laneCriteria: ILaneCriteria[]
): IDropStrategyOption[] => {
  return laneCriteria.map(criteria => {
    if (criteria.isPuDrop === true && criteria.isDelDrop === true) {
      return {
        label: 'Drop - Drop',
        value: DropTypes.dropDrop,
      }
    }
    if (criteria.isPuDrop === true && criteria.isDelDrop === false) {
      return {
        label: 'Drop - Live',
        value: DropTypes.dropLive,
      }
    }
    if (criteria.isPuDrop === false && criteria.isDelDrop === true) {
      return {
        label: 'Live - Drop',
        value: DropTypes.liveDrop,
      }
    }
    return {
      label: 'Live - Live',
      value: DropTypes.liveLive,
    }
  })
}

export const findLaneCriteriaByDropType = (
  laneCriteria: ILaneCriteria[],
  dropType: DropTypes
): ILaneCriteria | undefined => {
  if (dropType === DropTypes.dropDrop) {
    return laneCriteria.find(criteria => criteria.isPuDrop && criteria.isDelDrop)
  }
  if (dropType === DropTypes.dropLive) {
    return laneCriteria.find(criteria => criteria.isPuDrop && !criteria.isDelDrop)
  }
  if (dropType === DropTypes.liveDrop) {
    return laneCriteria.find(criteria => !criteria.isPuDrop && criteria.isDelDrop)
  }
  return laneCriteria.find(criteria => !criteria.isPuDrop && !criteria.isDelDrop)
}

export const formatCriteriaCondition = (
  criteriaCondition: ICriteriaCondition,
  modifier?: (n: string | number) => string
): string => {
  const { condition, value } = criteriaCondition
  const dataModifier =
    modifier ||
    function (n) {
      return n.toString()
    }
  const formattedValue = dataModifier(value)

  switch (condition) {
    case ConditionTypes.equal:
      return `${formattedValue}`
    case ConditionTypes.greaterThan:
      return `Greater than ${formattedValue}`
    case ConditionTypes.lessThan:
      return `Less than ${formattedValue}`
    case ConditionTypes.greaterThanOrEqualTo:
      return `Greater than or equal to ${formattedValue}`
    case ConditionTypes.lessThanOrEqualTo:
      return `Less than or equal to ${formattedValue}`
    default:
      return `${formattedValue}`
  }
}

export const getDropStrategy = (criteria: ILaneCriteria): DropTypes => {
  if (criteria.isPuDrop === true && criteria.isDelDrop === true) {
    return DropTypes.dropDrop
  }
  if (criteria.isPuDrop === true && criteria.isDelDrop === false) {
    return DropTypes.dropLive
  }
  if (criteria.isPuDrop === false && criteria.isDelDrop === true) {
    return DropTypes.liveDrop
  }
  return DropTypes.liveLive
}

export const getDropTypeDisplayValue = (criteria: ILaneCriteria): string => {
  const dropStrategy = getDropStrategy(criteria)

  if (dropStrategy === DropTypes.dropDrop) {
    return 'Drop - Drop'
  }
  if (dropStrategy === DropTypes.dropLive) {
    return 'Drop - Live'
  }
  if (dropStrategy === DropTypes.liveDrop) {
    return 'Live - Drop'
  }
  return 'Live - Live'
}

export const getEquipmentValue = (criteria: ILaneCriteria | null | undefined): string[] => {
  return criteria && criteria.equipment ? criteria.equipment : []
}

export const getEquipmentDisplayValue = (criteria: ILaneCriteria | null | undefined): string => {
  const equipments = getEquipmentValue(criteria)
  if (equipments.length > 0) {
    return equipments
      .map(equip => {
        const option = equipmentOptions.find(opt => opt.value === equip)
        return option ? option.label : null
      })
      .filter(equip => equip !== null)
      .join(', ')
  }
  return 'N/A'
}

export const getRiskyAuditValues = (criteria: ILaneCriteria | null | undefined): string[] => {
  return criteria && criteria.liveAuditRiskLevel ? criteria.liveAuditRiskLevel : []
}

export const getRiskyAuditDisplayValue = (criteria: ILaneCriteria | null | undefined): string => {
  const riskyAuditValues = getRiskyAuditValues(criteria)

  if (riskyAuditValues.length === 0) {
    return 'N/A'
  }

  return riskyAuditValues
    .map(value => {
      switch (value) {
        case RiskyAuditLevels.level1:
          return '1'
        case RiskyAuditLevels.level2:
          return '2'
        case RiskyAuditLevels.level3:
          return '3'
        case RiskyAuditLevels.level4:
          return '4'
        case RiskyAuditLevels.level5:
          return '5'
        case RiskyAuditLevels.level6:
          return '6'
        case RiskyAuditLevels.manualAudit:
          return 'Manual'
        case RiskyAuditLevels.doNotQuote:
          return 'DNQ'
        default:
          return value
      }
    })
    .join(', ')
}

export const getContractDurationValue = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria && criteria.contractDurationInMonths ? criteria.contractDurationInMonths : null
}

export const getContractDurationDisplayValue = (
  criteria: ILaneCriteria | null | undefined
): string => {
  const contractDuration = getContractDurationValue(criteria)

  if (contractDuration) {
    const modifier = (n: string | number) => `${n} months`
    return formatCriteriaCondition(contractDuration, modifier)
  }

  return 'N/A'
}

export const getPuMarketCenterDistance = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.puDistMarketCenter || null
}

export const getPuMarketCenterDistanceDisplayValue = (
  criteria: ILaneCriteria | null | undefined
): string => {
  const puMarketCenterDistance = getPuMarketCenterDistance(criteria)

  if (puMarketCenterDistance) {
    const modifier = (n: string | number) => `${n} miles`
    return formatCriteriaCondition(puMarketCenterDistance, modifier)
  }

  return 'N/A'
}

export const getDelMarketCenterDistance = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.delDistMarketCenter || null
}

export const getDelMarketCenterDistanceDisplayValue = (
  criteria: ILaneCriteria | null | undefined
): string => {
  const delMarketCenterDistance = getDelMarketCenterDistance(criteria)

  if (delMarketCenterDistance) {
    const modifier = (n: string | number) => `${n} miles`
    return formatCriteriaCondition(delMarketCenterDistance, modifier)
  }

  return 'N/A'
}

export const getAnnualVolume = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.annualVolume || null
}

export const getAnnualVolumeDisplayValue = (criteria: ILaneCriteria | null | undefined): string => {
  const annualVolume = getAnnualVolume(criteria)

  if (annualVolume) {
    const modifier = (n: string | number) => `${n} loads`
    return formatCriteriaCondition(annualVolume, modifier)
  }

  return 'N/A'
}

export const getMarketDemandScore = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.marketDemandScore || null
}

export const getMarketDemandScoreDisplayValue = (
  criteria: ILaneCriteria | null | undefined
): string => {
  const marketDemandScore = getMarketDemandScore(criteria)

  if (marketDemandScore) {
    return formatCriteriaCondition(marketDemandScore)
  }

  return 'N/A'
}

export const getExtraStops = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.extraStops || null
}

export const getExtraStopsDisplayValue = (criteria: ILaneCriteria | null | undefined): string => {
  const extraStops = getExtraStops(criteria)

  if (extraStops) {
    return formatCriteriaCondition(extraStops)
  }

  return 'N/A'
}

export const getEffectiveMileage = (
  criteria: ILaneCriteria | null | undefined
): ICriteriaCondition | null => {
  return criteria?.effectiveMileage || null
}

export const getEffectiveMileageDisplayValue = (
  criteria: ILaneCriteria | null | undefined
): string => {
  const effectiveMileage = getEffectiveMileage(criteria)

  if (effectiveMileage) {
    const modifier = (n: string | number) => `${n} miles`
    return formatCriteriaCondition(effectiveMileage, modifier)
  }

  return 'N/A'
}

export const getDatVariance = (
  strategy: Strategy | null | undefined
): ICriteriaCondition | null => {
  return strategy?.strategyMatrix?.varianceFlags?.datAnchor || null
}

export const getDatVarianceDisplayValue = (strategy: Strategy | null | undefined): string => {
  const datVariance = getDatVariance(strategy)

  if (datVariance) {
    const modifier = (n: string | number) => `${n}%`
    return formatCriteriaCondition(datVariance, modifier)
  }

  return 'N/A'
}

export const getNegativeMarginRisk = (
  strategy: Strategy | null | undefined
): ICriteriaCondition | null => {
  return strategy?.strategyMatrix?.varianceFlags?.negMarginRisk || null
}

export const getNegativeMarginRiskDisplayValue = (
  strategy: Strategy | null | undefined
): string => {
  const negativeMarginRisk = getNegativeMarginRisk(strategy)

  if (negativeMarginRisk) {
    const modifier = (n: string | number) => (Number(n) < 0 ? `-$${Math.abs(Number(n))}` : `$${n}`)
    return formatCriteriaCondition(negativeMarginRisk, modifier)
  }

  return 'N/A'
}

export const getMinimumRate = (strategy: Strategy | null | undefined): string | null => {
  if (typeof strategy?.strategyMatrix?.minimumRate === 'string') {
    return strategy.strategyMatrix.minimumRate
  }
  return null
}

export const getLengthOfHaul = (strategy: Strategy | null | undefined): string | null => {
  if (typeof strategy?.strategyMatrix?.lengthOfHaul === 'string') {
    return strategy.strategyMatrix.lengthOfHaul
  }
  return null
}

export const getMinimumMarginDisplayValue = (strategy: Strategy | null | undefined): string => {
  const minimumRate = getMinimumRate(strategy)
  const lengthOfHaul = getLengthOfHaul(strategy)

  if (minimumRate === null || lengthOfHaul === null) {
    return 'N/A'
  }

  return `$${minimumRate} if Length of Haul is ${lengthOfHaul}`
}

export const canDisplayMinimumMargin = (strategy: Strategy | null | undefined): boolean => {
  return getMinimumRate(strategy) !== null && getLengthOfHaul(strategy) !== null
}

export const thresholdManagementFieldsPresent = (
  strategy: Strategy | null | undefined
): boolean => {
  if (getDatVariance(strategy) !== null) {
    return true
  }
  if (getNegativeMarginRisk(strategy) !== null) {
    return true
  }
  if (canDisplayMinimumMargin(strategy)) {
    return true
  }
  return false
}

// DA: recursively scans a matrix object for numeric
// values and converts them from floats to percentage strings.
// The recursion is necessary because not all matrix data returned
// by the BE is 2-dimensional, so we can't know the depth of the
// object ahead of time.
export const matrixFloatsToPercentages = (matrix: any): Record<string, any> => {
  return Object.keys(matrix).reduce((output, currentKey) => {
    if (typeof matrix[currentKey] === 'number') {
      output[currentKey] = String(floatToPercentage(Number(matrix[currentKey])))
    } else if (typeof matrix[currentKey] === 'object') {
      output[currentKey] = matrixFloatsToPercentages(matrix[currentKey])
    }

    return output
  }, {} as Record<string, any>)
}

// DA: recursively scans a matrix object for string percentages
// and converts them to numeric floats. The recursion is necessary
// because not all matrix data is 2-dimensional, so we can't
// know the depth of the object ahead of time.
export const matrixPercentagesToFloats = (matrix: any): Record<string, any> => {
  return Object.keys(matrix).reduce((output, currentKey) => {
    if (typeof matrix[currentKey] === 'string') {
      output[currentKey] = percentageToFloat(Number(matrix[currentKey]))
    } else if (typeof matrix[currentKey] === 'object') {
      output[currentKey] = matrixPercentagesToFloats(matrix[currentKey])
    }

    return output
  }, {} as Record<string, any>)
}

// DA: In order to present strategy data correctly
// on the front end, we need to cast all numeric values
// to strings and convert floats to percentages where necessary:
export const formatAllStrategyData = (strategyData: Strategy): Strategy => {
  const formattedStrategyData = cloneDeep(strategyData)

  const { strategyMatrix } = formattedStrategyData

  Object.values(ValidStrategyMatrices).forEach(matrixKey => {
    if (strategyMatrix[matrixKey] !== undefined) {
      strategyMatrix[matrixKey] = matrixFloatsToPercentages(strategyMatrix[matrixKey])
    }
  })

  if (strategyMatrix.lengthOfHaul !== undefined) {
    strategyMatrix.lengthOfHaul = strategyMatrix.lengthOfHaul.toString()
  }

  if (strategyMatrix.minimumRate !== undefined) {
    strategyMatrix.minimumRate = strategyMatrix.minimumRate.toString()
  }

  const { laneCriteria, varianceFlags } = strategyMatrix || {}

  if (laneCriteria && laneCriteria.length > 0) {
    laneCriteria.forEach((criteria: ILaneCriteria) => {
      Object.values(ValidCriteriaConditions).forEach(criteriaKey => {
        const currentObj = criteria[criteriaKey]
        if (currentObj !== undefined) {
          currentObj.value = currentObj.value.toString()
        }
      })
    })
  }

  // DA: the DAT anchor threshold is stored as a float,
  // but needs to be presented as a percentage:
  if (varianceFlags && varianceFlags.datAnchor) {
    varianceFlags.datAnchor.value = floatToPercentage(varianceFlags.datAnchor.value).toString()
  }

  if (varianceFlags && varianceFlags.negMarginRisk) {
    varianceFlags.negMarginRisk.value = varianceFlags.negMarginRisk.value.toString()
  }

  return formattedStrategyData
}

// DA: Numeric strategy data is stored in string format on the
// client side so that we can use it in text inputs, but needs
// to be converted back to numbers before we persist it to the BE:
export const serializeAllStrategyData = (strategyData: Strategy): Strategy => {
  const serializedStrategyData = cloneDeep(strategyData)

  const { strategyMatrix } = serializedStrategyData

  Object.values(ValidStrategyMatrices).forEach(matrixKey => {
    if (strategyMatrix[matrixKey] !== undefined) {
      strategyMatrix[matrixKey] = matrixPercentagesToFloats(strategyMatrix[matrixKey])
    }
  })

  if (strategyMatrix.lengthOfHaul !== undefined) {
    strategyMatrix.lengthOfHaul = Number(strategyMatrix.lengthOfHaul)
  }

  if (strategyMatrix.minimumRate !== undefined) {
    strategyMatrix.minimumRate = Number(strategyMatrix.minimumRate)
  }

  const { laneCriteria, varianceFlags } = strategyMatrix || {}

  if (laneCriteria && laneCriteria.length > 0) {
    laneCriteria.forEach((criteria: ILaneCriteria) => {
      Object.values(ValidCriteriaConditions).forEach(criteriaKey => {
        const currentObj = criteria[criteriaKey]
        if (currentObj !== undefined) {
          currentObj.value = Number(currentObj.value || '0')
        }
      })
    })
  }

  // DA: the DAT anchor threshold is presented as a percentage,
  // but needs to be stored as a float:
  if (varianceFlags && varianceFlags.datAnchor) {
    varianceFlags.datAnchor.value = percentageToFloat(Number(varianceFlags.datAnchor.value))
  }

  if (varianceFlags && varianceFlags.negMarginRisk) {
    varianceFlags.negMarginRisk.value = Number(varianceFlags.negMarginRisk.value)
  }

  return serializedStrategyData
}
